/**
 * @fileoverview `AppSidebar` — sidebar principale de la console GNPC.
 *
 * Basée sur Sidebar-07 (shadcn), colorisée en navy Quipux via les tokens
 * `--sidebar-*` de `packages/ui`. Contient la navigation principale du
 * backoffice, groupée par domaine (Vue d'ensemble, Opérations, Gestion,
 * Administration).
 *
 * Convention capacité :
 *   - Chaque item de nav aura, à terme, un `capability` associé.
 *   - Rendu conditionnel via `<CapabilityGuard>` (à livrer).
 *   - Rappel invariant : masquage UX only. Le backend refait la vérif
 *     en 403 sur mutation (Guide frontend §5.2).
 *
 * Les items sont masqués via `CapabilityGuard` pour le confort UX. La vraie
 * autorisation reste côté backend lors de l'intégration.
 */

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from '@gnpc/ui';
import {
  BarChart3,
  CreditCard,
  FileDown,
  History,
  KeyRound,
  LogOut,
  Scale,
  Server,
  Settings,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { CapabilityGuard } from '../auth/CapabilityProvider.tsx';
import type { Capability } from '../../data/backoffice.ts';

interface NavItem {
  label: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Capacité requise. Ne jamais remplacer par un rôle minimum. */
  capability?: Capability;
}

interface NavGroup {
  label: string;
  items: readonly NavItem[];
}

const NAV_BUTTON_CLASS =
  'h-10 gap-3 px-3 text-[15px] group-data-[collapsible=icon]:size-10! group-data-[collapsible=icon]:p-2.5! [&>svg]:size-5 [&>svg]:stroke-[2.25]';

const ACTIVE_NAV_BUTTON_CLASS =
  'bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90 data-[active=true]:bg-sidebar-primary data-[active=true]:text-sidebar-primary-foreground';

const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: "Vue d'ensemble",
    items: [
      { label: 'Dashboard', to: '/', icon: BarChart3, capability: 'dashboard:read' },
    ],
  },
  {
    label: 'Opérations',
    items: [
      { label: 'Paiements', to: '/payments', icon: CreditCard, capability: 'payment:read' },
      {
        label: 'Remboursements',
        to: '/refunds',
        icon: History,
        capability: 'refund:request',
      },
      {
        label: 'Rapprochement',
        to: '/reconciliation',
        icon: Scale,
        capability: 'reconciliation:read',
      },
      { label: 'Exports', to: '/exports', icon: FileDown, capability: 'export:create' },
    ],
  },
  {
    label: 'Supervision',
    items: [
      { label: 'Intégrations', to: '/integrations', icon: Server, capability: 'integrations:health' },
      { label: "Journal d'audit", to: '/audit', icon: ShieldCheck, capability: 'audit:read' },
    ],
  },
  {
    label: 'Administration',
    items: [
      { label: 'Agents', to: '/users', icon: Users, capability: 'user:read' },
      {
        label: 'Rôles et permissions',
        to: '/roles-permissions',
        icon: KeyRound,
        capability: 'roles:manage',
      },
    ],
  },
];

/**
 * Réplique la sémantique d'activation de `NavLink` : correspondance exacte
 * pour la racine (`end`), exacte ou par préfixe de segment pour les autres
 * routes (`/payments` reste actif sur `/payments/:id`). Calculé ici plutôt
 * que via le render-prop de `NavLink` : `SidebarMenuButton` doit être le
 * PARENT du lien (`asChild`) pour ne pas rendre un `<button>` dans un `<a>`
 * (interdit par la spec HTML — signalé par le validateur W3C).
 */
function isNavItemActive(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

export function AppSidebar(): React.JSX.Element {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <Sidebar variant="sidebar" collapsible="icon">
      {/* Landmark unique englobant TOUT le contenu de la sidebar (logo,
        * groupes de menu, footer) : axe `region` exige que chaque contenu
        * visible vive dans un landmark, y compris le logo et Paramètres.
        * Le flex colonne reprend la mise en page du conteneur shadcn. */}
      <nav aria-label="Navigation principale" className="flex h-full min-h-0 flex-col">
      <SidebarHeader>
        {/* Header custom, hors SidebarMenuButton, pour laisser le logo
          * Quipux respirer horizontalement (ratio ~2.2:1) sans être
          * étranglé dans un carré size-8. */}
        <div className="flex flex-col gap-2 px-2 py-2 group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:px-0">
          <div className="flex items-center justify-center px-2 py-2 group-data-[collapsible=icon]:hidden">
            <img
              src={`${import.meta.env.BASE_URL}brand/logo_quipux_blanc.png`}
              alt="Quipux Afrique"
              className="h-10 w-auto"
            />
          </div>
          {/* Version compacte : monogramme « G » — le favicon (wordmark
            * complet) devenait une tache illisible en 20 px. Fond en
            * `bg-primary` (orange foncé accessible) et non l'orange brand
            * clair : blanc sur #ff4800 échouait au ratio 4,5:1 (axe
            * color-contrast). */}
          <div className="hidden size-8 items-center justify-center rounded-md bg-primary group-data-[collapsible=icon]:flex">
            <span aria-hidden="true" className="text-sm font-bold text-primary-foreground">
              G
            </span>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <div className="flex flex-1 flex-col gap-2">
          {NAV_GROUPS.map((group, idx) => (
            <SidebarGroup key={group.label} className="gap-2 py-3">
              {idx > 0 && <SidebarSeparator className="mb-3" />}
              <SidebarGroupLabel className="mb-1 h-7 px-3 text-[13px]">
                {group.label}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className="gap-1.5">
                  {group.items.map((item) => {
                    const isActive = isNavItemActive(pathname, item.to);
                    return (
                      <CapabilityGuard key={item.to} capability={item.capability}>
                        <SidebarMenuItem>
                          {/* `asChild` : le bouton DEVIENT le lien — jamais
                            * un `<button>` descendant d'un `<a>` (invalide
                            * en HTML, signalé ×9 par le validateur W3C). */}
                          <SidebarMenuButton
                            asChild
                            isActive={isActive}
                            tooltip={item.label}
                            // Item actif : orange primaire pour contraste fort avec le
                            // navy du fond. Le défaut shadcn (`bg-sidebar-accent` =
                            // navy-800) reste trop subtil sur navy-950.
                            className={
                              isActive
                                ? `${NAV_BUTTON_CLASS} ${ACTIVE_NAV_BUTTON_CLASS}`
                                : NAV_BUTTON_CLASS
                            }
                          >
                            {/* `NavLink` (et non `Link`) pour conserver
                              * `aria-current="page"` sur l'item actif. */}
                            <NavLink to={item.to} end={item.to === '/'}>
                              <item.icon />
                              <span>{item.label}</span>
                            </NavLink>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      </CapabilityGuard>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </div>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu className="gap-1.5">
          <SidebarMenuItem>
            {/* Navigation pure → vrai lien (`asChild`), pas un bouton avec
              * `navigate()` : sémantique correcte + ouverture dans un nouvel
              * onglet possible. */}
            <SidebarMenuButton asChild tooltip="Paramètres" className={NAV_BUTTON_CLASS}>
              <Link to="/account/profile">
                <Settings />
                <span>Paramètres</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            {/* Reste un vrai `<button>` : la déconnexion est une ACTION
              * (invalidation de session à terme), pas une navigation. */}
            <SidebarMenuButton
              tooltip="Se déconnecter"
              onClick={() => navigate('/login')}
              className={NAV_BUTTON_CLASS}
            >
              <LogOut />
              <span>Se déconnecter</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      </nav>
    </Sidebar>
  );
}
